import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { AdminFetchFeeWithdrawalReport } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { FeeWithdrawal } from 'src/interfaces/feeWithdrawal'
import { getFeeWithdrawalReportLoadingIds } from 'src/selectors/feeWithdrawals'
import { saveBlobAsFile } from 'src/utils/fileDownload'
import { buildFeeWithdrawalReportFileName } from 'src/utils/paymentsOps/feeWithdrawals'

interface FeeWithdrawalReportPreview {
  withdrawal: FeeWithdrawal
  blob: Blob
  /** Object URL of `blob`; revoked when the preview closes or the page unmounts. */
  url: string
}

interface FeeWithdrawalReportControls {
  /** Withdrawals whose report is being fetched. */
  reportLoadingIds: Record<string, true>
  preview: FeeWithdrawalReportPreview | null
  downloadReport(withdrawal: FeeWithdrawal): Promise<void>
  previewReport(withdrawal: FeeWithdrawal): Promise<void>
  downloadPreview(): void
  closePreview(): void
}

/**
 * Fetches a withdrawal's report PDF (through the saga, bearer token included) and either saves it
 * or keeps it for the preview modal. A failure — including "No report stored for this
 * withdrawal" on a 404 — is shown as an error toast.
 */
export const useFeeWithdrawalReport = (): FeeWithdrawalReportControls => {
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const reportLoadingIds = useSelector(getFeeWithdrawalReportLoadingIds)
  const [preview, setPreview] = useState<FeeWithdrawalReportPreview | null>(null)
  const isMountedRef = useRef<boolean>(true)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Each preview owns its object URL: replacing, closing or unmounting releases it.
  useEffect(() => {
    if (!preview) {
      return
    }

    return () => {
      window.URL.revokeObjectURL(preview.url)
    }
  }, [preview])

  const fetchReport = useCallback(
    (withdrawal: FeeWithdrawal) => {
      return asyncDispatchWithErrorToast<Blob>(
        AdminFetchFeeWithdrawalReport.request({ feeWithdrawalId: withdrawal.id }),
      )
    },
    [asyncDispatchWithErrorToast],
  )

  const downloadReport = useCallback(
    async (withdrawal: FeeWithdrawal) => {
      const blob = await fetchReport(withdrawal)

      if (!blob) {
        return
      }

      saveBlobAsFile(blob, buildFeeWithdrawalReportFileName(withdrawal))
    },
    [fetchReport],
  )

  const previewReport = useCallback(
    async (withdrawal: FeeWithdrawal) => {
      const blob = await fetchReport(withdrawal)

      // A URL created after unmount would never be revoked.
      if (!blob || !isMountedRef.current) {
        return
      }

      setPreview({ withdrawal, blob, url: window.URL.createObjectURL(blob) })
    },
    [fetchReport],
  )

  const downloadPreview = useCallback(() => {
    if (!preview) {
      return
    }

    saveBlobAsFile(preview.blob, buildFeeWithdrawalReportFileName(preview.withdrawal))
  }, [preview])

  const closePreview = useCallback(() => {
    setPreview(null)
  }, [])

  return useMemo(() => {
    return {
      reportLoadingIds,
      preview,
      downloadReport,
      previewReport,
      downloadPreview,
      closePreview,
    }
  }, [closePreview, downloadPreview, downloadReport, preview, previewReport, reportLoadingIds])
}
